import { PaginationItemProps, PaginationProps } from '../components/pagination/pagination'
import { ProductListItemProps } from '../components/product-list-item/product-list-item'
import { ProductVariantRowProps } from '../components/product-variant-row/product-variant-row'
import { ProductsListSectionProps } from '../modules/checkout/products-list-section'
import { EventView, Product, ProductCategory } from '../types'
import { formatPrice } from '../utils/format-price'
import { debouncedSetProductQuantity } from './product-utils'

const PRODUCTS_PER_PAGE = 20
const DESCRIPTION_MAX_LENGTH = 100

type CategoryProductListItem = ProductListItemProps & { category?: number }

const getProductsPage = (
    view: EventView,
    products: Product[],
    pageNumber: number,
): CategoryProductListItem[] => {
    const { update, state } = view
    const { lang } = state
    const productsByCategories: Map<number, CategoryProductListItem[]> = new Map<
        number,
        CategoryProductListItem[]
    >()
    const uncategorizedProducts: CategoryProductListItem[] = []

    for (const product of products) {
        // TODO implement modal
        const isTooLong = false // product.description.length > DESCRIPTION_MAX_LENGTH

        const total = product.variants.reduce((acc, variant) => {
            return acc + variant.unitPrice * variant.quantity
        }, 0)

        const productListItem: ProductListItemProps = {
            id: product.id,
            image: {
                srcset: product.image.srcset,
            },
            name: product.name,
            description: isTooLong
                ? product.description.slice(0, DESCRIPTION_MAX_LENGTH) + '...'
                : product.description,
            variants: product.variants.map(
                (variant): ProductVariantRowProps => ({
                    ...variant,
                    quantityInput: {
                        id: variant.id + '-quantity',
                        value: variant.quantity,
                        onChange: async (newValue: number) => {
                            debouncedSetProductQuantity(view, variant.id, newValue)
                            variant.quantity = newValue
                            update()
                        },
                        min: 0,
                        max: variant.maxQuantity ?? 99,
                        isDisabled: state.isLoading,
                    },
                    unitPrice: formatPrice(variant.unitPrice),
                    totalPrice: formatPrice(variant.unitPrice * variant.quantity),
                }),
            ),
        }

        if (total) {
            productListItem.total = {
                label: lang.products_total,
                price: formatPrice(total),
            }
        }

        if (isTooLong) {
            productListItem.readMore = {
                text: 'Loe rohkem',
            }
        }

        if (product.categories.length === 0) {
            uncategorizedProducts.push(productListItem)
        } else {
            const categoryId = state.selectedCategory
                ? Number(state.selectedCategory)
                : product.categories[0]

            const currentCategoryProducts = productsByCategories.get(categoryId) ?? []
            productsByCategories.set(categoryId, [
                ...currentCategoryProducts,
                {
                    ...productListItem,
                    category: categoryId,
                },
            ])
        }
    }

    if (uncategorizedProducts.length > 0) {
        productsByCategories.set(-1, uncategorizedProducts)
    }

    const startIndex = (pageNumber - 1) * PRODUCTS_PER_PAGE
    const endIndex = startIndex + PRODUCTS_PER_PAGE

    return Array.from(productsByCategories.values()).flat().slice(startIndex, endIndex)
}

const getPaginationProps = (view: EventView, products: Product[]): PaginationProps => {
    const { state, update } = view
    const { productsPageNumber } = state

    const totalPages = Math.ceil(products.length / PRODUCTS_PER_PAGE)

    const pages: PaginationItemProps[] = []

    for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
        pages.push({
            text: String(pageNumber),
            onClick: () => {
                state.productsPageNumber = pageNumber
                setTimeout(() => {
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    })
                }, 100)
                update()
            },
            isCurrent: pageNumber === productsPageNumber,
        })
    }

    return {
        previous: {
            text: '',
            onClick: () => {
                state.productsPageNumber -= 1
                setTimeout(() => {
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    })
                }, 100)
                update()
            },
            isDisabled: productsPageNumber === 1,
        },
        next: {
            text: '',
            onClick: () => {
                state.productsPageNumber += 1
                setTimeout(() => {
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    })
                }, 100)
                update()
            },
            isDisabled: productsPageNumber === totalPages,
        },
        pages,
    }
}

export const getProductsList = (view: EventView): ProductsListSectionProps => {
    const { state, update } = view
    const {
        productCategories,
        products,
        productsPageNumber,
        lang,
        selectedCategory,
        productSearch,
    } = state

    let filteredProducts = products

    if (selectedCategory) {
        filteredProducts = filteredProducts.filter((product) =>
            product.categories.includes(Number(selectedCategory)),
        )
    }

    if (productSearch) {
        filteredProducts = filteredProducts.filter(
            (product) =>
                product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
                (product.description &&
                    product.description.toLowerCase().includes(productSearch.toLowerCase())),
        )
    }

    const pagedProducts = getProductsPage(view, filteredProducts, productsPageNumber)

    let productsByCategories: Record<string, ProductListItemProps[]> = {}

    // We do this so the order is the same as the map which is important
    if (productCategories) {
        productCategories.forEach((category: ProductCategory) => {
            productsByCategories[category.name] = []
        })
    }

    for (const product of pagedProducts) {
        const categoryId = product.category ?? 0
        const categoryName =
            productCategories?.get(categoryId)?.name ?? lang['uncategorizedProducts']

        productsByCategories[categoryName] = [
            ...(productsByCategories[categoryName] ?? []),
            product,
        ]
    }
    // See comment above regarding sort order
    productsByCategories = Object.fromEntries(
        Object.entries(productsByCategories).filter(([, value]) => value.length > 0),
    )

    const props: ProductsListSectionProps = {
        productsByCategories,
    }

    if (filteredProducts.length > PRODUCTS_PER_PAGE) {
        props.pagination = getPaginationProps(view, filteredProducts)
    }

    if (!filteredProducts.length) {
        props.noResults = {
            texts: {
                noResults: lang.products_noResults,
                tryAnotherSearch: lang.products_tryAnotherSearch,
                removeFilters: lang.products_removeFilters,
            },
            removeFilters: () => {
                delete state.selectedCategory
                delete state.productSearch
                update()
            },
        }
    }

    return props
}
